/**
 * Layer IDs used to turn on and off specific rendering or raycasting features
 * per-object.
 */
export enum RenderLayerID {
  /**
   * The default layer ID used by ThreeJS and react-three-fiber. Objects
   * in this layer will render, but are not collidable (i.e. they will not
   * appear "hoverable" to the user). Most objects should be in this layer.
   */
  Default = 0,

  /**
   * An AssetViz-specific layer ID for all "hoverable" objects which should be
   * visible to the user. Careful consideration should be used when adding
   * objects to this layer, as it will increase objects and raycasting overhead.
   */
  VisibleCollidable = 1,

  /**
   * An AssetViz-specific layer ID for all objects which should be visible but
   * not "hoverable." This prevents excessive raycasting.
   */
  VisibleNonCollidable = 2,

  /**
   * An AssetViz-specific layer ID for all objects which should not be visible
   * but should be "hoverable." The most common use case for this layer is
   * shell courses.
   */
  InvisibleCollidable = 3,

  /**
   * An AssetViz-specific layer ID for all invisible and non-"hoverable"
   * objects. This includes HTML Overlay objects, which are only used to
   * track the position of labels in 3D space. By putting these objects in
   * this layer, the renderer and raycaster have less work to do.
   */
  InvisibleNonCollidable = 4,
}

/**
 * An enum to ensure that all "overlay" objects (heatmaps, grids, markup, etc.)
 * are rendered on top of each other in the correct order
 */
export enum RenderOrder {
  /** The ThreeJS-defined default render order */
  Default = 0,
  /** The photogrammetry texture layer */
  Photogrammetry = 1,
  /** The heatmap overlay layer */
  Heatmap = 2,
  /** The heatmap numeric text layer */
  TextLayer = 3,
  /** The markup overlay layer */
  Markup = 4,
  /** The selected feature overlay layer */
  SelectedFeature = 5,
  /** Debug colors layer */
  Debug = 6,
}

/**
 * Properties to apply to all "overlay" objects (heatmaps, grids, markup, etc.)
 * in order to consistently display in front of components' surface geometry.
 */
export const overlayMaterialDefines = {
  // Overlays should not contribute to the z-buffer
  depthWrite: false,
  // Even though not all overlays are transparent, we need to force them into
  // the transparent render pass to ensure they're rendered in front of other
  // transparent objects (heatmaps, etc.)
  transparent: true,
  // For certain transparent objects, it's necessary to render in two passes;
  // none of our use cases require this, so force a single pass for performance
  forceSinglePass: true,
}
